import {
    OutputSerializer,
    parseAssetRefPath,
    parseMagicMd,
    resolveDocumentAssetPath,
    scanDocumentText,
    type DocumentScanResult,
    type ImportBinding,
    type ImportCachedSymbol,
    type ParsedDocument,
    type SerializerTargetLocator,
    type UseBinding,
} from "@magic-md/core";
import { ASSET_REF_FULL_RE, DEFINE_HEADER_RE } from "@magic-md/core/patterns";
import { collectParsedDocumentUseBindings } from "./for-scope-units";

const WINDOWS_DRIVE_RE = /^[a-zA-Z]:\//;
const MAX_IMPORT_SOURCE_DEPTH = 12;

export interface ImportOriginStep {
    namespace: string;
    importSource: string;
    sourceDocumentPath: string;
    renderedId: string;
    publicName: string;
    sourceUnitId: string;
}

export interface ImportedUnitEditTarget {
    kind: "imported-symbol";
    unitId: string;
    namespace: string;
    importSource: string;
    sourceDocumentPath: string;
    sourceUnitId: string;
    publicName: string;
    chain: ImportOriginStep[];
}

export type UnitEditTarget =
    | {
        kind: "local-definition";
        unitId: string;
        locator?: SerializerTargetLocator;
    }
    | {
        kind: "local-use";
        unitId: string;
        sourceId: string;
        locator?: SerializerTargetLocator;
    }
    | ImportedUnitEditTarget
    | { kind: "unknown"; unitId: string };

export type SerializerUnitKind = "define" | "content" | "for" | "use";

/**
 * Editor-owned locator with a source identity captured when the user opens an
 * editing surface. Core serializers intentionally ignore `fingerprint`; the
 * editor resolver uses it to refuse stale/ambiguous occurrence fallbacks.
 */
export interface EditorSerializerTargetLocator extends SerializerTargetLocator {
    fingerprint?: string;
    candidateCount?: number;
    /** Exact source token used only when multiple candidates share a fingerprint. */
    documentFingerprint?: string;
}

export interface EditorTargetProvenance {
    tabId: string;
    paneId?: string;
    filePath?: string;
    unitKind?: SerializerUnitKind;
}

export function isEditorTargetProvenanceCurrent(
    target: EditorTargetProvenance | null | undefined,
    current: { tabId: string; paneId?: string; filePath?: string },
): boolean {
    if (!target) return true;
    return target.tabId === current.tabId
        && (!target.paneId || target.paneId === current.paneId)
        && (!target.filePath || target.filePath === current.filePath);
}

export interface SerializerTargetSourceRange {
    /** One-based source line, matching parser `range.start`. */
    start: number;
    /** One-based source line, matching parser `range.end`. */
    end: number;
}

interface ScannedUnitRange {
    id: string;
    startLine: number;
    endLine: number;
    outputRange?: { start: number; end: number };
}

function scannedUnitsForKind(
    scan: DocumentScanResult,
    unitKind: SerializerUnitKind,
): ScannedUnitRange[] {
    switch (unitKind) {
        case "define":
            return scan.defines;
        case "content":
            return scan.contentDefines;
        case "for":
            return scan.forBlocks;
        case "use":
            return scan.useBlocks;
    }
}

function lineInLocator(line: number, locator: SerializerTargetLocator): boolean {
    return line >= (locator.startLine ?? 0)
        && line <= (locator.endLine ?? Number.POSITIVE_INFINITY);
}

function targetFingerprint(
    content: string,
    unitKind: SerializerUnitKind,
    unit: ScannedUnitRange,
): string {
    const lines = content.split(/\r?\n/);
    const outputStart = unit.outputRange ? unit.outputRange.start - 1 : -1;
    const outputEnd = unit.outputRange ? unit.outputRange.end + 1 : -1;
    const stableLines: string[] = [];
    for (let line = unit.startLine; line <= unit.endLine; line++) {
        // Output is runtime-owned and may be inserted/replaced while a config
        // surface is open. Excluding the complete fence keeps identity stable
        // across those updates while retaining config/body differences that
        // distinguish same-ID source blocks.
        if (outputStart >= 0 && line >= outputStart && line <= outputEnd) continue;
        stableLines.push(lines[line] ?? "");
    }
    return `${unitKind}\u0000${unit.id}\u0000${stableLines.join("\n")}`;
}

/** Capture both the current header line and a shift-resistant occurrence hint. */
export function createSerializerTargetLocatorFromLine({
    content,
    unitKind,
    unitId,
    line,
}: {
    content: string;
    unitKind: SerializerUnitKind;
    unitId: string;
    line: number;
}): EditorSerializerTargetLocator | undefined {
    const candidates = scannedUnitsForKind(scanDocumentText(content), unitKind)
        .filter((unit) => unit.id === unitId)
        .sort((a, b) => a.startLine - b.startLine);
    const occurrence = candidates.findIndex((unit) => unit.startLine === line);
    if (occurrence < 0) return undefined;
    const target = candidates[occurrence];
    return {
        startLine: line,
        endLine: line,
        ...(candidates.length > 1 ? { occurrence } : {}),
        fingerprint: targetFingerprint(content, unitKind, target),
        candidateCount: candidates.length,
        documentFingerprint: content,
    };
}

/**
 * Resolve a serializer locator against the current full document.
 *
 * The scanner already understands nested content/for scopes and returns
 * document-absolute ranges, so callers should use this instead of rescanning
 * line text themselves. An exact CodeMirror locator wins; parser source ranges
 * are the next-best hint. The fallback mirrors the consumer's parse semantics
 * (`Map#set` definitions use the last occurrence, while collected uses use the
 * first occurrence).
 */
export function resolveSerializerTargetLocator({
    content,
    unitKind,
    unitId,
    locator,
    sourceRange,
    fallbackOccurrence = "first",
    scan = scanDocumentText(content),
}: {
    content: string;
    unitKind: SerializerUnitKind;
    unitId: string;
    locator?: SerializerTargetLocator | null;
    sourceRange?: SerializerTargetSourceRange | null;
    fallbackOccurrence?: "first" | "last";
    /** Reuse a scan when resolving more than one target for the same content. */
    scan?: DocumentScanResult;
}): EditorSerializerTargetLocator | undefined {
    const candidates = scannedUnitsForKind(scan, unitKind)
        .filter((unit) => unit.id === unitId)
        .sort((a, b) => a.startLine - b.startLine);
    if (candidates.length === 0) return undefined;

    const fingerprint = (locator as EditorSerializerTargetLocator | undefined)?.fingerprint;
    if (locator && fingerprint) {
        const editorLocator = locator as EditorSerializerTargetLocator;
        if (
            editorLocator.candidateCount !== undefined
            && candidates.length !== editorLocator.candidateCount
        ) {
            return undefined;
        }

        const identityMatches = candidates.filter(
            (unit) => targetFingerprint(content, unitKind, unit) === fingerprint,
        );
        if (identityMatches.length === 1) {
            return {
                startLine: identityMatches[0].startLine,
                endLine: identityMatches[0].startLine,
                fingerprint,
                candidateCount: editorLocator.candidateCount,
                documentFingerprint: editorLocator.documentFingerprint,
            };
        }

        // Identical same-ID blocks are distinguishable only while the source
        // document is exactly the one from which the line locator was captured.
        // Any edit/reorder makes the physical identity unknowable.
        if (editorLocator.documentFingerprint === content) {
            const inRangeMatches = identityMatches.filter((unit) =>
                lineInLocator(unit.startLine, locator),
            );
            if (inRangeMatches.length === 1) {
                return {
                    startLine: inRangeMatches[0].startLine,
                    endLine: inRangeMatches[0].startLine,
                    fingerprint,
                    candidateCount: editorLocator.candidateCount,
                    documentFingerprint: editorLocator.documentFingerprint,
                };
            }
        }

        // The source block changed or its identity is no longer unique. Falling
        // back to an old occurrence would silently edit a different same-ID unit.
        return undefined;
    }

    if (locator) {
        const inRange = candidates.filter((unit) =>
            lineInLocator(unit.startLine, locator),
        );
        const matched = inRange.length === 1
            ? inRange[0]
            : inRange[locator.occurrence ?? 0];
        if (matched) {
            return { startLine: matched.startLine, endLine: matched.startLine };
        }
        const occurrenceFallback = locator.occurrence == null
            ? undefined
            : candidates[locator.occurrence];
        if (occurrenceFallback) {
            return {
                startLine: occurrenceFallback.startLine,
                endLine: occurrenceFallback.startLine,
            };
        }
    }

    if (sourceRange) {
        const sourceStartLine = sourceRange.start - 1;
        const sourceEndLine = sourceRange.end - 1;
        const exactRange = candidates.find(
            (unit) =>
                unit.startLine === sourceStartLine
                && unit.endLine === sourceEndLine,
        );
        const matched = exactRange
            ?? candidates.find((unit) => unit.startLine === sourceStartLine);
        if (matched) {
            return { startLine: matched.startLine, endLine: matched.startLine };
        }
    }

    const scopeRanges = [
        ...scan.contentDefines.map((unit) => ({
            startLine: unit.startLine,
            endLine: unit.endLine,
        })),
        ...scan.forBlocks.map((unit) => ({
            startLine: unit.startLine,
            endLine: unit.endLine,
        })),
    ];
    const documentOwners = candidates.filter((candidate) =>
        !scopeRanges.some(
            (scope) =>
                scope.startLine < candidate.startLine
                && candidate.startLine <= scope.endLine,
        ),
    );
    if (documentOwners.length === 1) {
        return {
            startLine: documentOwners[0].startLine,
            endLine: documentOwners[0].startLine,
        };
    }

    const fallback = fallbackOccurrence === "last"
        ? candidates[candidates.length - 1]
        : candidates[0];
    return { startLine: fallback.startLine, endLine: fallback.startLine };
}

/** Parse the concrete ::use block selected by an editor locator. */
export function resolveUseBindingAtLocator({
    content,
    useId,
    locator,
}: {
    content: string;
    useId: string;
    locator: SerializerTargetLocator;
}): UseBinding | undefined {
    const scan = scanDocumentText(content);
    const resolvedLocator = resolveSerializerTargetLocator({
        content,
        unitKind: "use",
        unitId: useId,
        locator,
        scan,
    });
    if (resolvedLocator?.startLine == null) return undefined;
    const scannedUse = scan.useBlocks.find(
        (unit) =>
            unit.id === useId
            && unit.startLine === resolvedLocator.startLine,
    );
    if (!scannedUse) return undefined;

    const lines = content.split(/\r?\n/);
    const snippet = lines
        .slice(scannedUse.startLine, scannedUse.endLine + 1)
        .join("\n");
    const binding = parseMagicMd(snippet).uses?.[0];
    if (!binding) return undefined;
    return {
        ...binding,
        range: {
            start: scannedUse.startLine + 1,
            end: scannedUse.endLine + 1,
        },
    };
}

export interface ResolvedImportedUnitSource {
    sourceDocumentPath: string;
    sourceUnitId: string;
    topLevelNamespace: string;
    chain: ImportOriginStep[];
}

function normalizeSlashPath(path: string): string {
    return path.replace(/\\/g, "/");
}

function normalizeAbsolutePath(rawPath: string): string {
    const normalized = normalizeSlashPath(rawPath);
    const isAbsolute = normalized.startsWith("/") || WINDOWS_DRIVE_RE.test(normalized);
    const parts = normalized.split("/");
    const resolved: string[] = [];
    for (const part of parts) {
        if (!part || part === ".") continue;
        if (part === ".." && resolved.length > 0 && resolved[resolved.length - 1] !== "..") {
            resolved.pop();
            continue;
        }
        resolved.push(part);
    }
    if (WINDOWS_DRIVE_RE.test(normalized)) return resolved.join("/");
    return `${isAbsolute ? "/" : ""}${resolved.join("/")}`;
}

export function resolveImportSourcePath(
    documentPath: string,
    sourcePath: string,
): string {
    const assetMatch = ASSET_REF_FULL_RE.exec(sourcePath);
    const assetRef = assetMatch ? parseAssetRefPath(assetMatch[1]) : null;
    if (assetRef && !assetRef.namespace) {
        const resolved = resolveDocumentAssetPath(documentPath, assetRef.relativePath);
        if (resolved) return resolved;
    }

    if (sourcePath.startsWith("/") || WINDOWS_DRIVE_RE.test(sourcePath)) {
        return normalizeAbsolutePath(sourcePath);
    }

    const normalizedDocument = normalizeSlashPath(documentPath);
    const lastSlash = normalizedDocument.lastIndexOf("/");
    const dir = lastSlash >= 0 ? normalizedDocument.slice(0, lastSlash) : ".";
    return normalizeAbsolutePath(`${dir}/${sourcePath}`);
}

function symbolSourceUnitId(symbol: ImportCachedSymbol | undefined, fallback: string): string {
    return symbol?.originalId ?? symbol?.publicName ?? fallback;
}

function findImportedTargetInDocument(
    parsedDocument: ParsedDocument | null | undefined,
    documentPath: string | null | undefined,
    unitId: string,
): ImportedUnitEditTarget | null {
    if (!parsedDocument?.imports?.length || !documentPath) return null;

    for (const imp of parsedDocument.imports) {
        const symbol = imp.output?.symbols?.find((entry) => entry.qualifiedId === unitId);
        if (!symbol) continue;
        const sourceDocumentPath =
            imp.output?.document?.resolvedPath ??
            resolveImportSourcePath(documentPath, imp.source);
        const sourceUnitId = symbolSourceUnitId(symbol, symbol.publicName);
        const step: ImportOriginStep = {
            namespace: imp.namespace,
            importSource: imp.source,
            sourceDocumentPath,
            renderedId: unitId,
            publicName: symbol.publicName,
            sourceUnitId,
        };
        return {
            kind: "imported-symbol",
            unitId,
            namespace: imp.namespace,
            importSource: imp.source,
            sourceDocumentPath,
            sourceUnitId,
            publicName: symbol.publicName,
            chain: [step],
        };
    }

    const slashIndex = unitId.indexOf("/");
    if (slashIndex <= 0) return null;
    const namespace = unitId.slice(0, slashIndex);
    const rest = unitId.slice(slashIndex + 1);
    const imp = parsedDocument.imports.find((entry) => entry.namespace === namespace);
    if (!imp || !rest) return null;
    const sourceDocumentPath =
        imp.output?.document?.resolvedPath ??
        resolveImportSourcePath(documentPath, imp.source);
    const step: ImportOriginStep = {
        namespace,
        importSource: imp.source,
        sourceDocumentPath,
        renderedId: unitId,
        publicName: rest,
        sourceUnitId: rest,
    };
    return {
        kind: "imported-symbol",
        unitId,
        namespace,
        importSource: imp.source,
        sourceDocumentPath,
        sourceUnitId: rest,
        publicName: rest,
        chain: [step],
    };
}

export function resolveUnitEditTarget({
    unitId,
    parsedDocument,
    documentPath,
    content,
    locator,
    sourceRange,
}: {
    unitId: string | null | undefined;
    parsedDocument: ParsedDocument | null | undefined;
    documentPath: string | null | undefined;
    content?: string | null;
    locator?: SerializerTargetLocator | null;
    sourceRange?: SerializerTargetSourceRange | null;
}): UnitEditTarget | null {
    if (!unitId) return null;
    const scan = content != null ? scanDocumentText(content) : undefined;

    if (scan && locator) {
        const locatedUse = resolveSerializerTargetLocator({
            content: content ?? "",
            unitKind: "use",
            unitId,
            locator,
            scan,
        });
        const locatedDefinition = resolveSerializerTargetLocator({
            content: content ?? "",
            unitKind: "define",
            unitId,
            locator,
            fallbackOccurrence: "last",
            scan,
        });
        if (locatedUse) {
            const useBinding = resolveUseBindingAtLocator({
                content: content ?? "",
                useId: unitId,
                locator: locatedUse,
            });
            if (useBinding) {
                return {
                    kind: "local-use",
                    unitId,
                    sourceId: useBinding.source,
                    locator: locatedUse,
                };
            }
        }
        if (locatedDefinition) {
            return {
                kind: "local-definition",
                unitId,
                locator: locatedDefinition,
            };
        }
    }

    if (parsedDocument?.definitions?.has(unitId)) {
        const resolvedLocator = scan
            ? resolveSerializerTargetLocator({
                content: content ?? "",
                unitKind: "define",
                unitId,
                locator,
                sourceRange,
                fallbackOccurrence: "last",
                scan,
            })
            : undefined;
        return {
            kind: "local-definition",
            unitId,
            ...(resolvedLocator ? { locator: resolvedLocator } : {}),
        };
    }

    const useBinding = collectParsedDocumentUseBindings(parsedDocument).find(
        (entry) => entry.id === unitId,
    );
    if (useBinding) {
        const resolvedLocator = scan
            ? resolveSerializerTargetLocator({
                content: content ?? "",
                unitKind: "use",
                unitId,
                locator,
                sourceRange: sourceRange ?? useBinding.range,
                scan,
            })
            : undefined;
        return {
            kind: "local-use",
            unitId,
            sourceId: useBinding.source,
            ...(resolvedLocator ? { locator: resolvedLocator } : {}),
        };
    }

    return (
        findImportedTargetInDocument(parsedDocument, documentPath, unitId) ??
        { kind: "unknown", unitId }
    );
}

export async function resolveImportedUnitSource({
    unitId,
    parsedDocument,
    documentPath,
    readFile,
    maxDepth = MAX_IMPORT_SOURCE_DEPTH,
}: {
    unitId: string;
    parsedDocument: ParsedDocument | null | undefined;
    documentPath: string;
    readFile: (filePath: string) => Promise<string>;
    maxDepth?: number;
}): Promise<ResolvedImportedUnitSource | null> {
    let currentUnitId = unitId;
    let currentDocumentPath = documentPath;
    let currentParsedDocument: ParsedDocument | null | undefined = parsedDocument;
    const chain: ImportOriginStep[] = [];
    const seen = new Set<string>();

    for (let depth = 0; depth < maxDepth; depth += 1) {
        const seenKey = `${currentDocumentPath}\u0000${currentUnitId}`;
        if (seen.has(seenKey)) break;
        seen.add(seenKey);

        const target = findImportedTargetInDocument(
            currentParsedDocument,
            currentDocumentPath,
            currentUnitId,
        );
        if (!target) break;
        chain.push(target.chain[0]);

        const firstSegment = target.sourceUnitId.split("/")[0];
        const sourceContent = target.sourceUnitId.includes("/")
            ? await readFile(target.sourceDocumentPath)
            : null;
        const sourceParsed = sourceContent ? parseMagicMd(sourceContent) : null;
        const hasNestedImport = !!sourceParsed?.imports?.some(
            (entry: ImportBinding) => entry.namespace === firstSegment,
        );
        if (!hasNestedImport) {
            return {
                sourceDocumentPath: target.sourceDocumentPath,
                sourceUnitId: target.sourceUnitId,
                topLevelNamespace: chain[0].namespace,
                chain,
            };
        }

        currentUnitId = target.sourceUnitId;
        currentDocumentPath = target.sourceDocumentPath;
        currentParsedDocument = sourceParsed;
    }

    if (chain.length === 0) return null;
    const last = chain[chain.length - 1];
    return {
        sourceDocumentPath: last.sourceDocumentPath,
        sourceUnitId: last.sourceUnitId,
        topLevelNamespace: chain[0].namespace,
        chain,
    };
}

function escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function computeConfigPatch(
    draftConfig: Record<string, unknown>,
    sourceConfig: Record<string, unknown>,
): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(draftConfig)) {
        if (JSON.stringify(value) !== JSON.stringify(sourceConfig[key])) {
            patch[key] = value;
        }
    }
    return patch;
}

function collectTakenIds(parsedDocument: ParsedDocument | null | undefined): Set<string> {
    const ids = new Set<string>();
    for (const id of parsedDocument?.definitions?.keys() ?? []) ids.add(id);
    for (const id of parsedDocument?.contentDefinitions?.keys() ?? []) ids.add(id);
    for (const id of parsedDocument?.forDefinitions?.keys() ?? []) ids.add(id);
    for (const use of collectParsedDocumentUseBindings(parsedDocument)) ids.add(use.id);
    return ids;
}

function sanitizeUnitId(id: string): string {
    const sanitized = id.replace(/[^\w-]+/g, "-").replace(/^-+|-+$/g, "");
    return sanitized || "importedUnit";
}

export function suggestLocalUseId(
    target: ImportedUnitEditTarget,
    parsedDocument: ParsedDocument | null | undefined,
): string {
    const sourceTail = target.sourceUnitId.split("/").filter(Boolean).at(-1);
    const publicTail = target.publicName.split("/").filter(Boolean).at(-1);
    const base = sanitizeUnitId(sourceTail ?? publicTail ?? target.unitId);
    const taken = collectTakenIds(parsedDocument);
    if (!taken.has(base)) return base;
    const useBase = `${base}Use`;
    if (!taken.has(useBase)) return useBase;
    for (let i = 2; i < 1000; i += 1) {
        const candidate = `${useBase}${i}`;
        if (!taken.has(candidate)) return candidate;
    }
    return `${useBase}${Date.now()}`;
}

export function createLocalUseOverrideContent({
    content,
    parsedDocument,
    target,
    draftConfig,
    sourceConfig,
}: {
    content: string;
    parsedDocument: ParsedDocument | null | undefined;
    target: ImportedUnitEditTarget;
    draftConfig: Record<string, unknown>;
    sourceConfig: Record<string, unknown>;
}): { content: string; localUseId: string; patch: Record<string, unknown> } {
    const localUseId = suggestLocalUseId(target, parsedDocument);
    const patch = computeConfigPatch(draftConfig, sourceConfig);
    const config = Object.keys(patch).length > 0 ? { patch } : {};
    const block = [
        `::use[${localUseId}]{from="${target.unitId}"}`,
        "```config",
        JSON.stringify(config),
        "```",
        "::",
        "",
    ].join("\n");

    const escapedUnitId = escapeRegex(target.unitId);
    const renderMarker = new RegExp(
        `::render\\[${escapedUnitId}\\](?:\\{[^}]*\\})?::`,
    );
    const markerMatch = renderMarker.exec(content);
    const insertAt = markerMatch
        ? content.lastIndexOf("\n", markerMatch.index) + 1
        : content.length;
    const prefix = content.slice(0, insertAt);
    const suffix = content.slice(insertAt);
    const contentWithUse = `${prefix}${block}${suffix}`;
    const renderTarget = new RegExp(`(::render\\[)${escapedUnitId}(\\])`, "g");
    return {
        content: contentWithUse.replace(renderTarget, `$1${localUseId}$2`),
        localUseId,
        patch,
    };
}

export function removeDefinitionOutput(
    content: string,
    definitionId: string,
    locator?: SerializerTargetLocator,
): string {
    const resolvedLocator = resolveSerializerTargetLocator({
        content,
        unitKind: "define",
        unitId: definitionId,
        locator,
        fallbackOccurrence: "last",
    });
    if (resolvedLocator?.startLine == null) return content;

    const lines: string[] = [];
    const separators: string[] = [];
    let start = 0;
    for (const match of content.matchAll(/\r\n|\n/g)) {
        lines.push(content.slice(start, match.index));
        separators.push(match[0]);
        start = (match.index ?? start) + match[0].length;
    }
    lines.push(content.slice(start));
    separators.push("");

    const headerLine = resolvedLocator.startLine;
    const headerMatch = lines[headerLine]?.trim().match(DEFINE_HEADER_RE);
    if (!headerMatch || headerMatch[1] !== definitionId) return content;

    let j = headerLine + 1;
    let inFence = false;
    let fenceKind: "output" | "other" | "" = "";
    let activeFenceLen = 3;
    let outputStart = -1;
    let outputEnd = -1;

    while (j < lines.length) {
        const line = lines[j].trim();
        if (!inFence) {
            if (line === "::") break;
            const fenceMatch = line.match(/^(`{3,})([\w-]*)\s*$/);
            if (fenceMatch) {
                inFence = true;
                fenceKind = fenceMatch[2] === "output" ? "output" : "other";
                activeFenceLen = fenceMatch[1].length;
                if (fenceKind === "output") outputStart = j;
            }
        } else if (
            line.length === activeFenceLen
            && line === "`".repeat(activeFenceLen)
        ) {
            if (fenceKind === "output") outputEnd = j;
            inFence = false;
            fenceKind = "";
        }
        j += 1;
    }

    if (outputStart < 0 || outputEnd < 0) return content;
    lines.splice(outputStart, outputEnd - outputStart + 1);
    separators.splice(outputStart, outputEnd - outputStart + 1);
    return lines.map((line, index) => line + (separators[index] ?? "")).join("");
}

export function updateDefinitionConfigAndDropOutput(
    content: string,
    definitionId: string,
    config: Record<string, unknown>,
    locator?: SerializerTargetLocator,
): string {
    const serializer = new OutputSerializer();
    const resolvedLocator = resolveSerializerTargetLocator({
        content,
        unitKind: "define",
        unitId: definitionId,
        locator,
        fallbackOccurrence: "last",
    });
    if (!resolvedLocator) return content;
    return removeDefinitionOutput(
        serializer.updateConfig(content, definitionId, config, resolvedLocator),
        definitionId,
        resolvedLocator,
    );
}

export function updateDefinitionConfig(
    content: string,
    definitionId: string,
    config: Record<string, unknown>,
    locator?: SerializerTargetLocator,
): string {
    const serializer = new OutputSerializer();
    const resolvedLocator = resolveSerializerTargetLocator({
        content,
        unitKind: "define",
        unitId: definitionId,
        locator,
        fallbackOccurrence: "last",
    });
    if (!resolvedLocator) return content;
    return serializer.updateConfig(content, definitionId, config, resolvedLocator);
}
